/**
 * Hook: POST /api/t/{tenantSlug}/interview-analysis/skip — Phase 2.6.
 *
 * Marks a `(candidate × jd × stage)` analysis as skipped. Writes a marker
 * row with `transcript=NULL, analysis=NULL, skipped=TRUE`. No credit
 * debit (invariant A10).
 *
 * Phase 2.6 spec §7.5.
 *
 * Hard rules:
 *   - Skip is idempotent — re-skip is a no-op on the server.
 *   - On success, invalidate the paste-box-by-resource key so the
 *     skipped state renders immediately.
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";
import type { AnalysisPasteBoxRow } from "./useAnalysisPasteBox";

interface SkipArgs {
  candidate_id: string;
  jd_id: string;
  stage_id: string;
  /** Per-round slot (backend migration 0124). `null`/undefined = unassigned. */
  round_id?: string | null;
}

export function useSkipAnalysis(): UseMutationResult<
  AnalysisPasteBoxRow,
  ApiError,
  SkipArgs
> {
  const queryClient = useQueryClient();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  return useMutation<AnalysisPasteBoxRow, ApiError, SkipArgs>({
    mutationFn: async (args) => {
      const r = await apiClient.post<AnalysisPasteBoxRow>(
        `/api/t/${tenantSlug}/interview-analysis/skip`,
        {
          candidate_id: args.candidate_id,
          jd_id: args.jd_id,
          stage_id: args.stage_id,
          round_id: args.round_id ?? null,
        },
      );
      // Phase 2.6.1 (B5) — see useStartInterviewAnalysisParse.ts.
      return {
        ...r.data,
        saved_at: r.data.saved_at ?? new Date().toISOString(),
      };
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: [
          "interview-analysis",
          "paste-box",
          tenantSlug,
          vars.candidate_id,
          vars.jd_id,
          vars.stage_id,
          vars.round_id ?? null,
        ],
      });
    },
  });
}
